import type { GalleryCardType, GalleryDetailType } from "@volgo/core/contract";
import { EH_DEFAULT_SITE, EXH_DEFAULT_SITE } from "@volgo/core/eh";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { DownloadControls, DownloadNote, useGalleryDownload } from "../downloads/useGalleryDownload";
import { BookmarkPicker } from "../personal/BookmarkPicker";
import { ConfirmWarningButton, useAcks } from "../policy/ack";
import { useServerBlocks } from "../policy/blocked";
import {
  addTag,
  fromSearchParams,
  NAMESPACES,
  type SearchQuery,
  splitTag,
  toSearchParams,
} from "../search/query";
import { useSession } from "../session";
import { Button, buttonClass } from "../ui/Button";
import { CategoryMark } from "../ui/CategoryMark";
import { Dialog } from "../ui/Dialog";
import { describeError, isApiError } from "../ui/errors";
import { cancelFocusReturn, requestFocusReturn } from "../ui/focusReturn";
import { formatBytes, formatDate, formatRating } from "../ui/format";
import { Ambient, Cover, coverOf, tagNames } from "../ui/GalleryCard";
import { useGalleryItems } from "../ui/GallerySource";
import { Icon } from "../ui/icons";
import { NAMESPACE_LABELS, STATE_LABELS } from "../ui/labels";
import { ErrorNote, Loading } from "../ui/States";
import { useLoad } from "../ui/useLoad";
import { GalleryVideos } from "../videos/shared";
import { PagePreview, SameGroup } from "./GallerySections";

type Gallery = GalleryCardType | GalleryDetailType;
type TagUse = "only" | "include" | "exclude";

/** Tags by namespace, in E-Hentai's order; namespaces we do not know come last. */
function groupTags(tags: readonly string[]): [namespace: string, tags: string[]][] {
  const groups = new Map<string, string[]>();
  for (const tag of tags) {
    const { namespace } = splitTag(tag);
    groups.set(namespace, [...(groups.get(namespace) ?? []), tag]);
  }
  const order = (ns: string) => {
    const at = (NAMESPACES as readonly string[]).indexOf(ns);
    return at < 0 ? NAMESPACES.length : at;
  };
  return [...groups].sort((a, b) => order(a[0]) - order(b[0]));
}

function TagRows({
  tags,
  onUse,
  draft,
}: {
  tags: readonly string[];
  onUse: (tag: string, use: TagUse) => void;
  draft: SearchQuery | null;
}) {
  if (tags.length === 0) return null;
  return (
    <>
      <dl className="tags">
        {groupTags(tags).map(([namespace, list]) => (
          <div key={namespace} className="tags-row">
            <dt>{namespace ? (NAMESPACE_LABELS[namespace] ?? namespace) : "분류 없음"}</dt>
            <dd>
              {list.map((tag) => (
                <span
                  key={tag}
                  className="tg"
                  data-selection={
                    draft?.xtags.includes(tag) ? "exclude" : draft?.tags.includes(tag) ? "include" : undefined
                  }
                >
                  <button
                    type="button"
                    className="tg-name"
                    aria-pressed={draft?.tags.includes(tag) ?? false}
                    onClick={() => onUse(tag, "only")}
                  >
                    {splitTag(tag).name}
                  </button>
                  <button
                    type="button"
                    aria-label={`${tag} 지금 검색에 더하기`}
                    aria-pressed={draft?.tags.includes(tag) ?? false}
                    onClick={() => onUse(tag, "include")}
                  >
                    +
                  </button>
                  <button
                    type="button"
                    aria-label={`${tag} 지금 검색에서 제외`}
                    aria-pressed={draft?.xtags.includes(tag) ?? false}
                    onClick={() => onUse(tag, "exclude")}
                  >
                    −
                  </button>
                </span>
              ))}
            </dd>
          </div>
        ))}
      </dl>
      <p className="note">태그를 여러 개 고른 뒤 검색 버튼으로 적용하세요. +는 포함, −는 제외합니다.</p>
    </>
  );
}

function originalPage(gallery: Gallery): string {
  // Prefer ExH for unlisted galleries; this link does not prove availability.
  const site = gallery.state === "listed" ? EH_DEFAULT_SITE : EXH_DEFAULT_SITE;
  return `${site}/g/${gallery.gid}/${gallery.token}/`;
}

/**
 * Everything about one gallery. Order: cover and title with the facts, the buttons, the tags.
 * What may be shown is decided by the server's answer alone:
 *   no cover address  blocked: no picture, no download or read button
 *   policy warn       the confirm notice first; the cover and the buttons come after this gallery is confirmed
 * The newest answer wins: the card from the list only fills the window while the detail is on its way (and
 * never enables a button), and a refusal the server gives later (`POLICY_BLOCKED`) blocks the window at once.
 */
function GalleryBody({
  gid,
  fromList,
  onUseTag,
  draft,
  onApply,
}: {
  gid: number;
  fromList: GalleryCardType | undefined;
  onUseTag: (tag: string, use: TagUse) => void;
  draft: SearchQuery | null;
  onApply: () => void;
}) {
  const { client } = useSession();
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const acks = useAcks();
  const detail = useLoad(() => client.call("catalog.get", { params: { gid } }), String(gid));
  /** the server refused a download for want of a confirmation the page did not know was needed */
  const [demanded, setDemanded] = useState<string[] | null>(null);

  const blocks = useServerBlocks();
  const noteBlocked = blocks.note;

  /** the server has said this gallery is blocked: nothing below may hide that, and nothing may undo it */
  const refused = blocks.isBlocked(gid);

  // the list's card is shown only until the detail answers; a detail that failed is an error, not old data
  const current: Gallery | undefined = detail.data ?? (detail.status === "loading" ? fromList : undefined);
  // What this window showed last. Only a refused gallery falls back on it (or on the list's card), and of a
  // refused gallery only the title, the facts and the tags are drawn, so older data cannot show too much.
  const [last, setLast] = useState<Gallery | undefined>(current);
  useEffect(() => {
    if (current) setLast(current);
  }, [current]);
  const gallery: Gallery | undefined = current ?? (refused ? (last ?? fromList) : undefined);
  const fresh = detail.data;
  const freshBlocked = fresh !== undefined && coverOf(fresh) === null;
  useEffect(() => {
    if (freshBlocked) noteBlocked(gid);
  }, [freshBlocked, noteBlocked, gid]);
  const blocked = refused || (gallery !== undefined && coverOf(gallery) === null);
  const cover = gallery !== undefined && !blocked ? coverOf(gallery) : null;
  const warnTags =
    gallery?.policy.level === "warn" ? gallery.policy.matchedTags : demanded !== null ? demanded : null;
  const locked = warnTags !== null && !acks.isConfirmed(gid);
  // downloading and reading are offered on the detail's answer only, never on the older card from the list
  const canAct = detail.status === "ready" && !blocked && !locked;

  const onAckRequired = useCallback(
    (tags: string[]) => {
      acks.revoke(gid);
      setDemanded(tags);
    },
    [acks, gid],
  );
  const reloadDetail = detail.reload;
  const onBlocked = useCallback(() => {
    noteBlocked(gid);
    reloadDetail();
  }, [noteBlocked, gid, reloadDetail]);
  const dl = useGalleryDownload(gid, canAct, onAckRequired, onBlocked);
  const removed =
    gallery?.state === "removed" ||
    dl.failed?.errorCode === "GALLERY_REMOVED" ||
    isApiError(dl.error, "GALLERY_REMOVED");

  const blockedNotice = (
    <div className="notice" role="note">
      <b>차단된 태그가 포함되어 있습니다</b>
      <p>이 갤러리는 받거나 읽을 수 없습니다. 제목과 태그만 볼 수 있습니다.</p>
    </div>
  );

  if (!gallery) {
    // nothing to show of the gallery itself. A refusal the server already gave still comes first: the
    // loading and error screens are about the title and tags only, and say so.
    return (
      <div className="gw-solo" data-policy={refused ? "block" : undefined}>
        {refused && blockedNotice}
        {refused ? (
          detail.status === "error" ? (
            <p className="note" role="status">
              제목과 태그를 불러오지 못했습니다. <Button onClick={detail.reload}>다시 시도</Button>
            </p>
          ) : (
            <p className="note" role="status">
              제목과 태그를 받는 중입니다.
            </p>
          )
        ) : detail.status === "error" ? (
          <ErrorNote
            message={describeError(detail.error, {
              NOT_FOUND: "이 번호의 갤러리가 없습니다. 주소를 확인하거나 검색으로 찾아보세요.",
            })}
            onRetry={detail.reload}
          />
        ) : (
          <Loading />
        )}
      </div>
    );
  }

  return (
    <div className="gw" data-policy={blocked ? "block" : gallery.policy.level}>
      <div className="dw-hero">
        <div className={blocked ? "gw-cover hatch" : "gw-cover"}>
          {cover !== null && <Cover key={cover} src={cover} veiled={locked} />}
        </div>
        <div className="dw-head">
          <CategoryMark category={gallery.category} />
          <h2>{gallery.title}</h2>
          {gallery.titleJpn && (
            <p className="gw-jp" lang="ja">
              {gallery.titleJpn}
            </p>
          )}
          <p className="gid-line">
            <button
              type="button"
              className="gid-copy"
              aria-label={`GID ${gid} 복사`}
              title={copied ? "복사됨" : "GID 복사"}
              onClick={() => {
                setCopied(false);
                setCopyError(false);
                navigator.clipboard?.writeText(String(gid)).then(
                  () => setCopied(true),
                  () => setCopyError(true),
                ) ?? setCopyError(true);
              }}
            >
              GID {gid}
            </button>
            <span className="sr" role="status">
              {copied ? "복사됨" : ""}
            </span>
            {copyError && <span role="status">복사하지 못했습니다. GID를 선택해 복사하세요.</span>}
          </p>
          <dl className="facts">
            <div>
              <dt>쪽수</dt>
              <dd>{gallery.filecount}</dd>
            </div>
            <div>
              <dt>용량</dt>
              <dd>{formatBytes(gallery.filesize)}</dd>
            </div>
            {gallery.rating !== null && (
              <div>
                <dt>평점</dt>
                <dd>{formatRating(gallery.rating)}</dd>
              </div>
            )}
            {gallery.posted && (
              <div>
                <dt>올린 날</dt>
                <dd>{formatDate(gallery.posted)}</dd>
              </div>
            )}
            {gallery.uploader && (
              <div>
                <dt>올린이</dt>
                <dd>{gallery.uploader}</dd>
              </div>
            )}
            {gallery.state !== "listed" && (
              <div>
                <dt>상태</dt>
                <dd>{STATE_LABELS[gallery.state]}</dd>
              </div>
            )}
          </dl>
        </div>
        {/* the glow only behind a cover that is shown as it is: never blocked, never a veiled warning */}
        {cover !== null && !locked && <Ambient key={cover} src={cover} />}
      </div>
      <div className="gw-body">
        {blocked && blockedNotice}
        {!blocked && locked && warnTags !== null && (
          <div className="notice notice-warn" role="note">
            <b>경고 태그가 있습니다</b>
            <p>
              {warnTags.length > 0 ? `${tagNames(warnTags)} 태그가 붙은 갤러리입니다. ` : ""}
              확인하면 표지와 내려받기 버튼이 나옵니다.
            </p>
            <ConfirmWarningButton gid={gid} primary />
          </div>
        )}

        <div className="acts">
          {canAct && <DownloadControls dl={dl} removed={removed} />}
          <BookmarkPicker gid={gid} />
          {!blocked && !removed && (
            <a
              className={buttonClass("text")}
              href={originalPage(gallery)}
              target="_blank"
              rel="noreferrer noopener"
            >
              원본 페이지 열기
            </a>
          )}
        </div>
        {canAct && <DownloadNote dl={dl} removed={removed} />}

        <div className="tag-editor">
          <div>
            <TagRows tags={gallery.tags} onUse={onUseTag} draft={draft} />
          </div>
          <div className="tag-draft">
            {draft && (
              <Button variant="primary" onClick={onApply}>
                선택 태그로 검색
              </Button>
            )}
          </div>
        </div>

        {canAct && dl.done && <PagePreview gid={gid} />}
        {/* a blocked window shows no picture at all, other galleries' covers included (46 Spec 5) */}
        {!blocked && <GalleryVideos gid={gallery.gid} />}
        {!blocked && <SameGroup gallery={gallery} />}

        <div className="gw-foot">
          <span>
            <kbd>←</kbd> 앞 작품
          </span>
          <span>
            <kbd>→</kbd> 다음 작품
          </span>
          <span>
            <kbd>Esc</kbd> 닫기
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * The gallery window: opens over the list at `/g/:gid` and opens again on a reload of that address.
 * Left and right arrows move to the neighbouring gallery of the list, Esc closes.
 */
export function GalleryWindow({ gidParam, inApp }: { gidParam: string; inApp: boolean }) {
  const location = useLocation();
  const navigate = useNavigate();
  const items = useGalleryItems();
  const [draft, setDraft] = useState<SearchQuery | null>(null);

  const gid = /^[1-9][0-9]{0,14}$/.test(gidParam) ? Number(gidParam) : null;
  const index = gid === null ? -1 : items.findIndex((c) => c.gid === gid);

  /** the gallery the window was opened on: where focus goes if the last one shown has no link to go back to */
  const openedOn = useRef(gid);
  const openedFrom = useRef(
    document.activeElement instanceof HTMLElement && document.activeElement.closest(`[data-gid="${gid}"]`)
      ? document.activeElement
      : null,
  );
  const from = location.state as { depth?: number; listed?: number } | null;
  /** how many windows opened from "the same group" row lie between this one and the list */
  const depth = from?.depth ?? 0;
  /** a window opened from the row hands focus back to the card of the list's gallery it was opened over */
  const back = from?.listed ?? gid;

  const close = useCallback(() => {
    // The screen we go back to may be drawn afresh (favourites, history, downloads): its links do not exist
    // yet, so they are asked to take focus when they appear.
    if (back !== null) requestFocusReturn(openedOn.current === null ? [back] : [back, openedOn.current]);
    // opened from a screen of ours: go back to it, past the windows opened from "the same group" inside it;
    // opened by address: show the list under the window
    if (inApp) navigate(-1 - depth);
    else navigate({ pathname: "/", search: location.search }, { replace: true });
  }, [back, depth, inApp, navigate, location.search]);

  const go = useCallback(
    (step: 1 | -1) => {
      if (index < 0) return;
      const target = items[index + step];
      if (target) {
        navigate(
          { pathname: `/g/${target.gid}`, search: location.search },
          { replace: true, state: location.state },
        );
      }
    },
    [index, items, navigate, location.search, location.state],
  );

  const onKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      if (e.target instanceof HTMLElement && e.target.closest("input, select, textarea")) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        go(-1);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        go(1);
      }
    },
    [go],
  );

  const searchByTag = useCallback(
    (tag: string, use: TagUse) => {
      setDraft((previous) => {
        const current = previous ?? fromSearchParams(new URLSearchParams(location.search));
        if (use === "only" && current.tags.includes(tag))
          return { ...current, tags: current.tags.filter((t) => t !== tag) };
        return addTag(current, tag, use === "exclude" ? "exclude" : "include");
      });
    },
    [location.search],
  );
  const applyTags = () => {
    if (draft) navigate({ pathname: "/", search: toSearchParams(draft).toString() }, { replace: true });
  };

  // on closing, focus goes back to the card of the list's gallery that was open last
  const returnFocus = useCallback(() => {
    // A video page may link the same original from several cards. Prefer the actual trigger while its
    // background remains mounted; retain the gallery fallback for list navigation/remounted screens.
    const trigger = back === openedOn.current && openedFrom.current?.isConnected ? openedFrom.current : null;
    const link =
      trigger ?? (back === null ? null : document.querySelector<HTMLElement>(`[data-gid="${back}"] a`));
    // a link that is still there a moment later was not part of a screen being replaced: nothing is pending
    if (link) setTimeout(() => link.isConnected && cancelFocusReturn(), 0);
    return link ?? document.querySelector<HTMLElement>("main h1, main .sum");
  }, [back]);

  return (
    <Dialog
      label="갤러리 정보"
      className="drawer"
      drawer
      onClose={close}
      onKey={onKey}
      returnFocus={returnFocus}
    >
      <div className="dw-bar">
        <button
          type="button"
          className="iconbtn"
          aria-label="앞 작품"
          disabled={!items[index - 1] || index < 0}
          onClick={() => go(-1)}
        >
          <Icon name="left" />
        </button>
        <button
          type="button"
          className="iconbtn"
          aria-label="다음 작품"
          disabled={!items[index + 1] || index < 0}
          onClick={() => go(1)}
        >
          <Icon name="right" />
        </button>
        {index >= 0 && <span className="dw-pos">{`${index + 1} / ${items.length}`}</span>}
        <button type="button" className="iconbtn gw-close" aria-label="닫기" onClick={close}>
          <Icon name="x" />
        </button>
      </div>
      {gid === null ? (
        <div className="gw-solo">
          <ErrorNote message="주소의 갤러리 번호가 올바르지 않습니다. 검색으로 찾아보세요.">
            <Button onClick={close}>닫기</Button>
          </ErrorNote>
        </div>
      ) : (
        <GalleryBody
          key={gid}
          gid={gid}
          fromList={items[index]}
          onUseTag={searchByTag}
          draft={draft}
          onApply={applyTags}
        />
      )}
    </Dialog>
  );
}
